/* eslint-disable max-lines */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import { t } from 'i18next';

import { deserializer } from '~services/baseSerializers';
import { LANGUAGE } from '~constants/search';
import { CONFIG_COUNTRIES, CURRENT_COUNTRY_ID } from '~constants/environment';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.locale(LANGUAGE);

export const DAY_ENDS = '2000-01-01T23:59:59.999Z';
export const DAY_STARTS = '2000-01-01T00:00:00.000Z';
export const TIMER_SECOND = 1_000;
export const TIMER_MINUTE = TIMER_SECOND * 60;
export const TIMER_HOUR = TIMER_MINUTE * 60;
export const TIMER_DAY = TIMER_HOUR * 24;
export const TIMER_ENDED = ' 00:00:00';
export const TEXT_DAY = 'día';
export const TEXT_DAYS = 'días';

export const DAYS_OF_THE_WEEK = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday'
};

export const translateDay = (day, short = false) => {
  const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(
    day.toLowerCase()
  );

  dayjs.locale(LANGUAGE);

  return dayjs()
    .day(dayIndex)
    .format(short ? 'ddd' : 'dddd');
};

const TIME_ZONE = process.env.REACT_APP_TIME_ZONE;

export const DAYS = Object.values(DAYS_OF_THE_WEEK).map(day => day);

export const dateToTime = date => dayjs(date).utc().format('HH:mm');

export const getTodayString = locale =>
  dayjs()
    .locale(locale || '')
    .format('dddd')
    .toLowerCase();

const getRange = (opening, closing) =>
  opening === closing ? t('StoreDetail:allDay') : `${dateToTime(opening)} - ${dateToTime(closing)}`;

export const mergeSchedules = schedules => {
  const sortedSchedules = [...schedules].sort((a, b) => a.position - b.position);

  const allSchedules = sortedSchedules?.reduce((newSchedules, entry) => {
    const currentEntry = newSchedules.find(_entry => _entry?.startDay === entry.day);
    const range = getRange(entry.openingTime, entry.closingTime);
    let newEntry = null;

    if (currentEntry) {
      currentEntry.range = `${currentEntry.range}, ${range}`;
    } else {
      newEntry = {
        range,
        day: entry.day,
        startDay: translateDay(entry.day, true)
      };
    }

    newSchedules.push(newEntry);

    return newSchedules;
  }, []);

  return allSchedules.filter(Boolean);
};

export const normalizeBusinessHours = (data, waitTime, rawRanges = false) =>
  data?.reduce((businessHours, { day, id, openingTime, closingTime, dispatchMethod }) => {
    const currentDayIndex = DAYS.indexOf(day);
    const newEntries = businessHours?.[dispatchMethod] || [];
    const nextDayFirstEntry = data.find(
      entry =>
        DAYS.indexOf(entry.day) === currentDayIndex + 1 &&
        entry.openingTime === DAY_STARTS &&
        entry.dispatchMethod === dispatchMethod
    );
    const newClosingTime =
      closingTime === DAY_ENDS && nextDayFirstEntry?.openingTime === DAY_STARTS
        ? nextDayFirstEntry.closingTime
        : closingTime;

    const isDayEnds = newClosingTime === DAY_ENDS ? DAY_STARTS : newClosingTime;

    const newEntry = {
      id,
      day,
      openingTime,
      closingTime: rawRanges ? closingTime : isDayEnds,
      position: currentDayIndex
    };

    if ((openingTime === DAY_STARTS && closingTime === DAY_ENDS) || openingTime !== DAY_STARTS || rawRanges) {
      newEntries.push(newEntry);
    }

    businessHours[dispatchMethod] = newEntries;
    return businessHours;
  }, {});

export const formatDate = date => dayjs(date).format('DD/MM/YYYY');

export const buildDateES = date => dayjs(date).locale('es').format('D [de] MMMM [de] YYYY');

export const formatDateTimestamp = date => dayjs(date).tz(TIME_ZONE).format('DD/MM/YYYY, HH:mm');

export const currentTimestamp = () => dayjs().utc(Date.now());

export const handleTransformDate = date => {
  const originalDayjs = dayjs(date, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');

  const tz = CONFIG_COUNTRIES[CURRENT_COUNTRY_ID].timezone;

  const safeDate = dayjs.tz(
    {
      year: originalDayjs.year(),
      month: originalDayjs.month(),
      date: originalDayjs.date(),
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0
    },
    tz
  );

  return safeDate.toISOString();
};

export const businessHoursDelivery = (data, waitTime, pickUp) => {
  const serializedHours = data && deserializer.serialize(normalizeBusinessHours(data, waitTime, true));
  const weekBusinessHours = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  };
  serializedHours?.[pickUp ? 'inStore' : 'pjDelivery']?.map(item =>
    weekBusinessHours[item.day].push({
      id: item.id,
      openingTime: item.openingTime,
      closingTime: item.closingTime
    })
  );
  return weekBusinessHours;
};

export const dayFormat = stringDateFormat => {
  const date = dayjs(stringDateFormat);
  return `${date.format('dddd')}.${date.format('DD/MM')}`;
};

const RANGE = 10;

export const timeRangeFormat = stringDateFormat => {
  const date = dayjs(stringDateFormat);
  return `${date.format('HH:mm')} - ${date.add(RANGE, 'minutes').format('HH:mm')}`;
};

const dateToShowFormat = (date, day, number) =>
  ({
    0: t('SelectTimeDispatch:today'),
    1: t('SelectTimeDispatch:tomorrow')
  }[number] || `${day.format('dddd')} ${date.format('DD/MM')}`);

const LAST_HOUR = 24;
const COUNT_DAYS_WEEK = 7;
const QUARTER_AFTER_HOUR = 15;
const QUARTER_TILL_HOUR = 45;

const normalizeTime = ({ date, isOpeningTime }) => {
  let minutes = date.getMinutes();
  let hours = date.getHours();
  if (minutes === QUARTER_AFTER_HOUR) {
    minutes = isOpeningTime ? RANGE : 0;
  }
  if (minutes === QUARTER_TILL_HOUR) {
    hours += isOpeningTime ? 1 : 0;
    minutes = isOpeningTime ? 0 : RANGE;
  }
  date.setHours(hours);
  date.setMinutes(minutes);
};

const validEndTime = (endTime, limitPermited) => {
  const endTimeHours = endTime.hour() === 0 ? LAST_HOUR : endTime.hour();
  const isSameHour =
    endTimeHours === limitPermited.hour() ? endTime.minute() <= limitPermited.minute() : false;
  return endTimeHours >= limitPermited.hour() ? isSameHour : true;
};

const validStartTime = (startTime, waitTimeOffset) => {
  const isSameHour =
    startTime.hour() === waitTimeOffset.hour() ? startTime.minute() >= waitTimeOffset.minute() : false;
  return startTime.hour() > waitTimeOffset.hour() ? true : isSameHour;
};

const getDeliveryRanges = ({
  businessHours,
  waitTimeOffset,
  hoursPermited,
  limitPermited,
  deliveryWaitTime
}) =>
  businessHours.reduce((ranges, { openingTime, closingTime }) => {
    const openingTimeNormalize = new Date(openingTime);
    normalizeTime({ date: openingTimeNormalize, isOpeningTime: true });

    const closingTimeNormalize = new Date(closingTime);
    normalizeTime({ date: closingTimeNormalize });

    const openingTimeHours = new Date(openingTimeNormalize).getUTCHours();

    const closingTimeHours = closingTime === DAY_ENDS ? LAST_HOUR : closingTimeNormalize.getUTCHours();
    const lastHourPermited = closingTimeHours > hoursPermited ? hoursPermited : closingTimeHours;

    if (lastHourPermited > openingTimeHours) {
      Array(Math.ceil(dayjs(closingTimeNormalize).diff(dayjs(openingTimeNormalize), 'minutes') / RANGE))
        .fill()
        // eslint-disable-next-line array-callback-return
        .map((_, index) => {
          const startTime = dayjs(openingTimeNormalize)
            .utc()
            .add(RANGE * index, 'minutes')
            .add(deliveryWaitTime, 'minutes');

          const endTime = dayjs(openingTimeNormalize)
            .utc()
            .add(RANGE * (index + 1), 'minutes')
            .add(deliveryWaitTime, 'minutes');

          if (
            (waitTimeOffset ? validStartTime(startTime, waitTimeOffset) : true) &&
            (hoursPermited === LAST_HOUR || validEndTime(endTime, limitPermited)) &&
            (dayjs(closingTime).utc().diff(endTime) >= 0 ||
              (closingTime === DAY_ENDS && endTime.hour() === 0 && endTime.minute() === 0))
          ) {
            ranges.push({
              start: startTime,
              end: endTime,
              value: index,
              name: `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`.toLowerCase()
            });
          }
        });
    }
    return ranges;
  }, []);

export const getDaysRange = (hoursAmount, deliveryWaitTime, businessHours) => {
  const dateNow = dayjs();
  const limitPermited = dayjs().add(hoursAmount, 'hour');

  const normalizeDay =
    limitPermited.day() < dateNow.day() ? limitPermited.day() + COUNT_DAYS_WEEK : limitPermited.day();

  const daysDiff =
    normalizeDay - dateNow.day() === 0 && limitPermited.diff(dateNow, 'days') > 0 ? COUNT_DAYS_WEEK : 0;

  const daysRange = Array(Math.max(normalizeDay - dateNow.day(), daysDiff) + 1)
    .fill()
    .reduce((days, _, index) => {
      const date = dayjs().add(index, 'day');
      const day = date.locale('en').format('dddd').toLowerCase();

      if (!businessHours[day]) {
        return days;
      }

      const diffDays = limitPermited.day() - date.day();
      const diffHours = Math.floor(dayjs(limitPermited).diff(date, 'hour', true));

      const isSameDay = diffHours > LAST_HOUR ? LAST_HOUR : limitPermited.hour();
      const hoursPermited = diffDays === 0 ? isSameDay : LAST_HOUR;

      days.push({
        date,
        id: `${date}`,
        text: dateToShowFormat(date, day, index),
        deliveryRanges: getDeliveryRanges({
          businessHours: businessHours[day],
          waitTimeOffset: index === 0 ? date.minute(date.minute() + deliveryWaitTime) : null,
          hoursPermited,
          limitPermited,
          deliveryWaitTime
        })
      });

      return days;
    }, []);

  return daysRange.filter(item => !!item.deliveryRanges.length);
};

export const validateTimeRange = (startTime, endTime) => {
  const guessedTimeZone = dayjs.tz.guess();
  const now = dayjs().tz(guessedTimeZone);
  const today = now.format('YYYY-MM-DD');

  const startTimeParse = dayjs.tz(`${today} ${startTime}`, 'YYYY-MM-DD HH:mm', guessedTimeZone);
  const endTimeParse = dayjs.tz(`${today} ${endTime}`, 'YYYY-MM-DD HH:mm', guessedTimeZone);

  return now.isAfter(startTimeParse) && now.isBefore(endTimeParse);
};

export const getMinutesDifference = (startTime, endTime) => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  return dayjs(start).diff(end, 'minute', true);
};

export const getDateValue = date => dayjs(date).valueOf();

export const getTimeToFinish = timespan => {
  const days = Math.floor(timespan / TIMER_DAY);
  const hours = Math.floor((timespan / TIMER_HOUR) % 24);
  const minutes = Math.floor((timespan / TIMER_MINUTE) % 60);
  const seconds = Math.floor((timespan / TIMER_SECOND) % 60);

  const daysToFinish = days > 0 ? `${days} ${days === 1 ? TEXT_DAY : TEXT_DAYS}` : '';

  const formattedTime = [hours, minutes, seconds].map(unit => String(unit).padStart(2, '0')).join(':');

  return daysToFinish ? `${daysToFinish} ${formattedTime}` : formattedTime;
};

export function formatTimeToMinutesAndSeconds(time) {
  const minutes = `${Math.floor(time / 60)}`.padStart(2, '0');
  const seconds = `${time % 60}`.padStart(2, '0');
  return `${minutes}:${seconds}`;
}

export const getTodayScheduleFormatted = (
  businessHours,
  dispatchMethod = 'in_store',
  tz = 'America/Santiago'
) => {
  const now = dayjs().tz(tz);

  const dayInEnglish = now.locale('en').format('dddd').toLowerCase();
  const localizedDay = now.locale('es').format('dddd');
  const capitalizedDay = localizedDay.charAt(0).toUpperCase() + localizedDay.slice(1);

  const schedulesToday = businessHours.filter(
    h => h.day === dayInEnglish && h.dispatchMethod === dispatchMethod
  );

  if (schedulesToday.length === 0) {
    return null;
  }

  const timeRanges = schedulesToday.map(({ openingTime, closingTime }) => {
    const open = dayjs.utc(openingTime).format('HH:mm');
    const close = dayjs.utc(closingTime).format('HH:mm');
    return `${open} - ${close}`;
  });

  return `${capitalizedDay}: ${timeRanges.join(', ')}`;
};

export function nativeFormatDate(locale, options = {}) {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  });
}
